import { Card, Col, Form, Row, Select, Space, Typography, message } from "antd";
import { useForm } from "antd/lib/form/Form";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import useShop from "../../../../hooks/useShop";
import useSpecial from "../../../../hooks/useSpecial";
import useUser from "../../../../hooks/useUsers";
import sharedConstants from "../../../../shared/constants";
import { getDecodeToken } from "../../../../shared/utils/Token";
import color from "../../../../theme/color";
import { CustomRangePicker } from "../../DatePicker/Range";
import { FilterExtraBtnComp } from "../../Filter/Extra";
import { CustomForm } from "../../Form/index.styled";
import {
  GhostCar,
  GhostDelivery,
  GhostList,
  GhostPhone,
  GhostStore,
  GhostUser,
} from "../../Icon";
import { IconForm } from "../../Icon/index.styled";
import { SearchInput } from "../../Input/Search";
import { CustomSearchSelect } from "../../Select/Search";
import { CardTitle } from "../Detail/Card.styled";
import { ProductStatus } from "../Expended/Products/ProductStatus";
import { ReservationStatus } from "../Tags/Reservation";
import { fixFilter } from "../utils/filter";

interface ReservationFilterFormProps {
  defaultValue?: any;
  onSearch: (values: any) => void;
  onExpended?: (value: boolean) => void;
}
export const ReservationFilterForm: React.FC<ReservationFilterFormProps> = ({
  defaultValue,
  onSearch,
  onExpended = () => void 0,
}) => {
  const [form] = useForm();

  const { getAdminInfo, getUserID } = useUser();
  const { shop } = useShop();
  const { special } = useSpecial();

  const [visible, setVisible] = useState(false);
  const [errorStatus, setErrorStatus] = useState<boolean>(false);

  /**
   * 필터 수정
   * @param values
   */
  const handleFinish = (values: any) => {
    /**
     * 전체조회 불가하도록 체크
     * 1. 주문번호, 상품주문번호, 오픈마켓 주문번호 중 하나라도 있으면 pass
     * 2. 번호들이 없고,  주문날짜, 예약날짜, 생성날짜 중 하나라도 있으면 pass
     */
    message.destroy();
    const isNoFilter = !!values?.orderNo;
    const isDateFilter = !!values?.reservedAt;

    if (!isNoFilter && !isDateFilter) {
      message.error({
        content: (
          <Space size={0} align="center">
            <Typography.Text strong>주문번호</Typography.Text>
            <Typography.Text>를 입력하지 않으면&nbsp;</Typography.Text>
            <Typography.Text strong>예약일시</Typography.Text>
            <Typography.Text>는 필수입니다.</Typography.Text>
          </Space>
        ),
        duration: 10,
      });
      setErrorStatus(true);
      if (defaultValue) {
        // 사이클 연동을 위해 강제 처리
        form.setFieldsValue({ orderNo: defaultValue });
      }
    } else {
      setErrorStatus(false);

      const formdata = {
        ...values,
      };
      if (values.reservedAt && values.reservedAt.length === 2) {
        const [start, end] = values.reservedAt;

        formdata.reservedAtFrom = moment(start).startOf("day").toISOString();
        formdata.reservedAtTo = moment(end).endOf("day").toISOString();

        delete formdata.reservedAt;
      } else {
        formdata.reservedAtFrom = undefined;
        formdata.reservedAtTo = undefined;
      }
      onSearch(formdata);
    }
  };

  const handleKeyUp: React.KeyboardEventHandler<HTMLFormElement> = (event) => {
    if (event.key === "Enter") {
      form.submit();
    }
  };

  const handleReset = () => {
    form.resetFields();
    fixFilter(form);
    form.submit();
  };

  /**
   * 필터 수정 시 자동 조회
   */
  const filterDebounce = useCallback(
    debounce((filterChange) => {
      if (filterChange.length) {
        form.submit();
      }
    }, 500),
    []
  );

  /**
   * 스페셜코드 찾기
   */
  const specialCode: string | undefined = useMemo(() => {
    return special?.split("=")[1];
  }, [special]);

  /**
   * 스피드메이트 마스터거나 일반 매장 마스터계정
   */
  const master = useMemo(() => {
    const info = getDecodeToken();

    return (
      (info?.isMaster && +shop === getUserID()) ||
      (specialCode && sharedConstants.masterCodes.includes(specialCode))
    );
  }, [shop, specialCode]);

  /**
   * 출장교체 매장용 필터링
   * @notice 현재 출장교체매장은 타이어플라이 하나뿐이라 하드코딩중, 추 후 늘어난다면 수정 필요
   */
  const isOnsite = useMemo(() => {
    const userinfo = getAdminInfo();
    return userinfo?.repairShopType === "출장교체";
  }, []);

  useEffect(() => {
    onExpended(visible);
  }, [visible]);

  /**
   * 주문번호 클릭 처리
   */
  useEffect(() => {
    if (defaultValue) {
      form.setFieldsValue({ orderNo: defaultValue });
    } else {
      // 최근 30일 주문건만 조회 되도록
      fixFilter(form);
    }
    form.submit();
  }, [defaultValue]);

  return (
    <Card
      title={<CardTitle>필터</CardTitle>}
      extra={
        <FilterExtraBtnComp
          onReload={handleReset}
          expand={visible}
          onExpand={(expand) => setVisible(!!expand)}
          onSearch={form.submit}
        />
      }
      bordered={false}
      headStyle={{ padding: "0px 24px", borderBottomWidth: 0 }}
      bodyStyle={{
        padding: visible ? 16 : 0,
        visibility: visible ? "visible" : "hidden",
        height: visible ? "auto" : 0,
      }}
    >
      <CustomForm
        form={form}
        labelAlign="left"
        wrapperCol={{ span: 24 }}
        colon={false}
        onFinish={handleFinish}
        onFieldsChange={filterDebounce}
        onKeyUp={handleKeyUp}
      >
        <Row gutter={[6, 6]}>
          <Col span={6}>
            <Form.Item name="orderNo" hidden={!visible}>
              <SearchInput
                allowClear
                placeholder="주문번호"
                prefix={
                  <IconForm
                    component={GhostDelivery}
                    color={color.gray4}
                    style={{ fontSize: 14 }}
                  />
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="repairStatus" hidden={!visible}>
              <CustomSearchSelect
                allowClear
                prefix={
                  <IconForm
                    component={GhostList}
                    color={color.gray4}
                    style={{ fontSize: 12 }}
                  />
                }
                placeholder="예약상태"
                options={Object.entries(ReservationStatus)
                  .filter(([k]) => k !== "CANCELED_ORDER")
                  .map(([k, v]) => ({ label: v, value: k }))}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="reservedAt"
              hidden={!visible}
              validateStatus={errorStatus ? "error" : undefined}
            >
              <CustomRangePicker
                size="small"
                addOnBefore="예약일시"
                placeholder={["예약일시", ""]}
                format="YYYY-MM-DD"
                allowClear
                onChange={(values) => {
                  if (values && values.length === 2) {
                    form.setFieldsValue({
                      reservedAt: [
                        moment(values[0]).startOf("day"),
                        moment(values[1]).endOf("day"),
                      ],
                    });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="consumerName" hidden={!visible}>
              <SearchInput
                allowClear
                placeholder="고객명"
                prefix={
                  <IconForm
                    component={GhostUser}
                    color={color.gray4}
                    style={{ fontSize: 12 }}
                  />
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="consumerPhone" hidden={!visible}>
              <SearchInput
                allowClear
                placeholder="휴대폰번호"
                prefix={
                  <IconForm
                    component={GhostPhone}
                    color={color.gray4}
                    style={{ fontSize: 12 }}
                  />
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="vehicleNo" hidden={!visible}>
              <SearchInput
                allowClear
                placeholder="차량번호"
                prefix={
                  <IconForm
                    component={GhostCar}
                    color={color.gray4}
                    style={{ fontSize: 12 }}
                  />
                }
              />
            </Form.Item>
          </Col>
          {isOnsite === false && (
            <Col span={6}>
              <Form.Item name="isValet" hidden={!visible}>
                <CustomSearchSelect
                  prefix={
                    <IconForm
                      component={GhostStore}
                      color={color.gray4}
                      style={{ fontSize: 12 }}
                    />
                  }
                  placeholder="방문방법"
                  allowClear
                >
                  <Select.Option value={false}>고객방문</Select.Option>
                  <Select.Option value>발렛장착</Select.Option>
                </CustomSearchSelect>
              </Form.Item>
            </Col>
          )}
          {isOnsite === false && (
            <Col span={6}>
              <Form.Item name="productType" hidden={!visible}>
                <CustomSearchSelect
                  prefix={
                    <IconForm
                      component={GhostDelivery}
                      color={color.gray4}
                      style={{ fontSize: 14 }}
                    />
                  }
                  allowClear
                  placeholder="상품분류"
                  options={Object.entries(ProductStatus)
                    .filter(([k]) => k !== "ETC")
                    .map(([k, v]) => ({ label: v, value: k }))}
                />
              </Form.Item>
            </Col>
          )}
          {master && (
            <Col span={6}>
              <Form.Item name="displayName" hidden={!visible}>
                <SearchInput
                  allowClear
                  placeholder="매장명"
                  prefix={
                    <IconForm
                      component={GhostStore}
                      color={color.gray4}
                      style={{ fontSize: 12 }}
                    />
                  }
                />
              </Form.Item>
            </Col>
          )}
          {isOnsite === false && (
            <Col span={6}>
              <Form.Item name="trackingNo" hidden={!visible}>
                <SearchInput
                  allowClear
                  placeholder="운송장번호"
                  prefix={
                    <IconForm
                      component={GhostDelivery}
                      color={color.gray4}
                      style={{ fontSize: 14 }}
                    />
                  }
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </CustomForm>
    </Card>
  );
};
